import React, { useEffect, useId, useRef, useState } from 'react';
import { IconButton } from '@noths/polaris-design-system-core-components';
import type { ReviewPhoto } from '@noths/polaris-design-system-ooux-interfaces';

import type { CarouselNavigatePayload } from '../../../hooks/useCarouselNavigation';
import { useCarouselNavigation } from '../../../hooks/useCarouselNavigation';
import { NEXT_PHOTO, PHOTOS_LABEL, PREVIOUS_PHOTO } from './copy';
import * as styles from './ReviewCardPhotos.styles';

interface ReviewCardPhotosProps {
  initialPhotoIndex: number;
  onNavigate?: (payload: CarouselNavigatePayload) => void;
  photos: ReviewPhoto[];
}

/**
 * Every photo the customer attached, as a carousel.
 *
 * Every one, not the two the preview card has room for: the whole point of opening a
 * review is to see the rest. It opens on `initialPhotoIndex` so a customer who tapped the
 * third thumbnail lands on the third photo.
 *
 * The track is the same scroll-snap pattern `ProductCarousel` uses, and the navigation
 * comes from the same `useCarouselNavigation`, so a swipe and an arrow press report
 * through one path and settle once.
 */
export const ReviewCardPhotos = ({
  initialPhotoIndex,
  onNavigate,
  photos,
}: ReviewCardPhotosProps) => {
  const trackRef = useRef<HTMLUListElement>(null);
  const trackId = `review-card-photos${useId()}`;
  const { jumpTo, scrollNext, scrollPrev } = useCarouselNavigation(trackRef, onNavigate);
  const [currentIndex, setCurrentIndex] = useState(initialPhotoIndex);

  /* Jump to the photo that was tapped, without animating: the customer has just arrived,
     so a scroll from the first photo would be a transition from something they never
     saw. `jumpTo` rather than a bare `scrollLeft` assignment, so the hook does not report
     the jump as a swipe the customer never made, and rather than `scrollIntoView`, which
     would scroll the panel the card sits in as well as the track. */
  useEffect(() => {
    const track = trackRef.current;

    if (!track) {
      return;
    }

    jumpTo(track.clientWidth * initialPhotoIndex);
  }, [initialPhotoIndex, jumpTo]);

  /* A swipe moves the counter by way of the track, since a swipe is the only thing that
     tells us about it. Rounding to the nearest slide is what a half-swipe that snapped
     back should report. */
  const handleScroll = () => {
    const track = trackRef.current;

    if (!track || track.clientWidth === 0) {
      return;
    }

    setCurrentIndex(Math.round(track.scrollLeft / track.clientWidth));
  };

  /* An arrow press moves the counter itself rather than waiting to be told by the scroll
     it causes. The press is the only unambiguous signal we get: the scroll it starts is
     animated, so the counter and the disabled arrows would otherwise lag a press by the
     length of the animation, and would not move at all where the scroll is never
     reported. `handleScroll` still corrects this if a swipe lands somewhere else. */
  const scrollTo = (direction: 1 | -1) => {
    setCurrentIndex((index) => Math.min(Math.max(0, index + direction), photos.length - 1));

    if (direction === 1) {
      scrollNext();
    } else {
      scrollPrev();
    }
  };

  return (
    <div css={styles.photos}>
      <ul
        aria-label={PHOTOS_LABEL}
        aria-roledescription="carousel"
        css={styles.track}
        id={trackId}
        onScroll={handleScroll}
        ref={trackRef}
      >
        {photos.map((photo, index) => (
          <li
            aria-label={`${index + 1} of ${photos.length}`}
            aria-roledescription="slide"
            css={styles.slide}
            // eslint-disable-next-line react/no-array-index-key
            key={`${photo.url}-${index}`}
            role="group"
          >
            {/* Every photo but the one the customer tapped is lazy: that one is what the
                interaction was about, so deferring it would add a round trip to the only
                image on screen. The same escape hatch `ProductCardMedia` uses. */}
            <img
              alt={photo.alt}
              css={styles.photo}
              decoding="async"
              loading={index === initialPhotoIndex ? 'eager' : 'lazy'}
              src={photo.url}
              /* Spread and lowercase, the way `ProductCardMedia` does it: the lint rule
                 and the JSX types do not know `fetchpriority` yet. */
              {...(index === initialPhotoIndex ? { fetchpriority: 'high' } : {})}
            />
          </li>
        ))}
      </ul>

      {photos.length > 1 && (
        <>
          {/* Disabled rather than unmounted at either end, so the controls do not move
              around under the customer's thumb as they reach the last photo. */}
          <div css={styles.previousControl}>
            <IconButton
              aria-controls={trackId}
              aria-label={PREVIOUS_PHOTO}
              compact
              disabled={currentIndex === 0}
              icon="actionBack"
              onClick={() => scrollTo(-1)}
            />
          </div>
          <div css={styles.nextControl}>
            <IconButton
              aria-controls={trackId}
              aria-label={NEXT_PHOTO}
              compact
              disabled={currentIndex === photos.length - 1}
              icon="actionForward"
              onClick={() => scrollTo(1)}
            />
          </div>
          {/* Hidden from assistive technology: each slide already announces its own
              position, so a live counter would say the same thing twice. */}
          <p aria-hidden css={styles.counter} data-testid="review-card-photo-counter">
            {currentIndex + 1}/{photos.length}
          </p>
        </>
      )}
    </div>
  );
};

ReviewCardPhotos.displayName = 'ReviewCardPhotos';
